<template>
  <div v-if="!hasMeetingDeleted">
    <div class="bg-theme-secondary p-32 rounded">
      <div class="top-bar">
        <div class="flex items-center justify-between w-max">
          <div class="back-icon" @click="$router.go(-1)" v-if="isLoggedIn">
            <back-arrow class="m-auto right-back-arrow" />
          </div>
        </div>
          <div class="w-full">
            <div class="text-24 font-bold flex items-center">
              <span>{{ $t(activePage) }}</span>
            <span v-if="activePage !== 'meeting_detail'" class="font-normal text-subtitle">/</span>
              <span v-if="activePage !== 'meeting_detail'"
               class="text-subtitle block sm:w-200 w-500 truncate">
                <span v-if="meetingInfo.meeting_name">
                  {{ meetingInfo.meeting_name }}
                </span>
                <span v-else>{{ $t('busy') }}</span>
              </span>
            </div>
          </div>
      </div>
      <div class="meeting-detail-container">
        <meeting-detail-sidebar
          v-if="isLoggedIn && renderComponent"
          :newFormatDateStart="newFormatDateStart"
          :newFormatDateEnd="newFormatDateEnd"
          :calendar="calendar"
          @updateMeetingInfo="getMeetingInfo"
          :meetingInfo="meetingInfo"
          @changePage="changePageHandler"
          :activePage="activePage"
        />
        <div class="w-full">
          <KeepAlive v-if="renderComponent">
            <component
              v-if="meetingInfo.id"
              :is="activeComponent"
              :meetingInfo="meetingInfo"
              @getMeetingInfo="getMeetingInfo"
              :newFormatDateStart="newFormatDateStart"
              :newFormatDateEnd="newFormatDateEnd"
              :calendar="calendar"
            />
          </KeepAlive>
        </div>
      </div>
    </div>
    <div
      class="introduction"
      v-if="!isLoggedIn"
      :style="{ 'background-image': 'url(' + `${WaveImage}` + ')' }"
    >
      <div class="flex flex-col justify-center items-center h-300 px-20">
        <div class="flex flex-col gap-20">
          <span class="font-bold text-title">{{ $t('introduction_access_title') }}</span>
          <p class="break-word">{{ $t('introduction_access_text') }}</p>
          <a href="https://novaday.ir/nova-access/" target="_blank">
            <div
              class="border border-primary rounded-sm text-primary
               h-32 w-132 flex justify-center items-center cursor-pointer bg-secondary"
            >
              {{ $t('show_more') }}
            </div>
          </a>
        </div>
      </div>
      <div class="flex flex-col justify-center items-center h-300 px-20">
        <div class="flex flex-col">
          <span class="font-bold text-title">{{ $t('introduction_access_features') }}</span>
          <ul class="bullet-style">
            <li>{{ $t('access_feature_one') }}</li>
            <li>{{ $t('access_feature_two') }}</li>
            <li>{{ $t('access_feature_three') }}</li>
            <li>{{ $t('access_feature_four') }}</li>
          </ul>
        </div>
      </div>
      <div class="p-20 h-300 px-20">
        <div
          class="bg-contain bg-no-repeat w-full h-full"
          :style="{ 'background-image': 'url(' + `${Introduction}` + ')' }"
        ></div>
      </div>
    </div>
  </div>
  <div v-else class="bg-theme-secondary p-32 rounded flex justify-center items-center h-500">
    <div class="flex flex-col items-center gap-y-20">
      <delete-icon width="40" height="40" class="popover-img" :color="'var(--color-subtitle)'" />
      <p class="text-subtitle">{{ $t('deleted_meeting') }}</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import MeetingDetailSidebar from '@/views/conference/MeetingDetailSidebar.vue';
import MeetingDetailInfo from '@/views/conference/MeetingDetailInfo.vue';
import BackArrow from '@/assets/icons/vue/backArrow.vue';
import axiosConfig from '@/services/axios.config';
import serverErrorsHandler from '@/utils/serverErrorsHandler';
import DeleteIcon from '@/assets/icons/vue/trash.vue';
import WaveImage from '@/assets/icons/png/waveImage.png';
import Introduction from '@/assets/icons/png/Introduction.png';

const moment = require('moment-jalaali');

export default {
  name: 'MeetingDetail',
  components: {
    MeetingDetailSidebar,
    BackArrow,
    MeetingDetailInfo,
    DeleteIcon,
    DuplicateMeeting: () => import('@/views/conference/DuplicateMeeting.vue'),
    EditMeeting: () => import('@/views/conference/EditMeeting.vue'),
    MeetingChat: () => import('@/views/conference/MeetingChat.vue'),
    CustomReminder: () => import('@/views/conference/CustomReminder.vue'),
  },
  data() {
    return {
      WaveImage,
      Introduction,
      meetingInfo: {},
      meetingInfoID: this.$route.params.reserveId,
      meetingCode: this.$route.params.meetingCode,
      deviceId: this.$route.params.deviceId,
      activeComponent: MeetingDetailInfo,
      activePage: 'meeting_detail',
      companyId: this.$route.params.companyId,
      newFormatDateStart: '',
      newFormatDateEnd: '',
      calendar: 'Solar',
      renderComponent: true,
      hasMeetingDeleted: false,
    };
  },
  computed: {
    ...mapGetters({
      isLoading: 'loading/getActiveSkeletons',
    }),
    isLoggedIn() {
      return !!this.$store.state.authentication.loggedIn;
    },
    shareLinkUrl() {
      if (this.$route.query.t) {
        return `/m/${this.meetingCode}?t=${this.$route.query.t}`;
      }
      return `/m/${this.meetingCode}`;
    },
  },
  watch: {
    calendar() {
      this.newFormatDateHandler();
    },
    $route(routeInfo) {
      if (routeInfo.name === 'MeetingDetail') {
        this.meetingInfoID = routeInfo.params.reserveId;
        this.companyId = routeInfo.params.companyId;
        this.deviceId = routeInfo.params.deviceId;
        this.getMeetingInfo();
        this.activePage = 'meeting_detail';
        this.activeComponent = 'meetingDetailInfo';
        this.renderComponent = false;
        this.$nextTick(() => {
          this.renderComponent = true;
        });
      }
    },
  },
  created() {
    this.getMeetingInfo();
  },
  methods: {
    getMeetingInfo() {
      if (this.meetingCode && this.$route.name === 'SharedMeeting') {
      this.sharedMeetingInfoHandler();
    } else {
      this.updateMeetingInfo();
    }
    },
    getSettings() {
      axiosConfig
        .get('/user/get-setting')
        .then((response) => {
          this.calendar = response.data.data.calendar_type;
          this.newFormatDateHandler();
        })
        .catch((error) => {
          serverErrorsHandler(error);
        });
    },
    changePageHandler(page) {
      this.activePageHandler(page);
      this.activeComponent = page;
    },
    activePageHandler(page) {
      // eslint-disable-next-line default-case
      switch (page) {
        case 'MeetingDetailInfo':
          this.activePage = 'meeting_detail';
          break;
        case 'EditMeeting':
          this.activePage = 'edit_meeting';
          break;
        case 'MeetingChat':
          this.activePage = 'discussion';
          break;
        case 'DuplicateMeeting':
          this.activePage = 'duplicate_meeting';
          break;
        case 'CustomReminder':
          this.activePage = 'custom_reminder';
          break;
      }
    },
    updateMeetingInfo() {
      this.activePage = 'meeting_detail';
      this.activeComponent = MeetingDetailInfo;
      this.renderComponent = false;
      this.$nextTick(() => {
        this.renderComponent = true;
      });
      this.$store.dispatch('loading/skeletonLoader', {
        name: 'meetingAttendees',
        status: true,
      });
      axiosConfig
        .get(
          `/companies/${this.companyId || this.meetingInfo.company_id}/reservation/${
            this.meetingInfoID || this.meetingInfo.id
          }`,
        )
        .then((response) => {
          this.$store.dispatch('loading/skeletonLoader', {
            name: 'meetingAttendees',
            status: false,
          });
          this.meetingInfo = response.data.data;
          this.getSettings();
        })
        .catch((error) => {
          this.$store.dispatch('loading/skeletonLoader', {
            name: 'meetingAttendees',
            status: false,
          });
          if (error.response.status === 404) {
            this.hasMeetingDeleted = true;
          }
          if (error.response.status === 403) {
            this.$toast.error(this.$t('confidential_meeting_toast'));
            this.$router.push({
              name: 'Dashboard',
              params: { locale: localStorage.getItem('locale') },
              query: { active: 'dashboard' },
            });
          }
        });
    },
    sharedMeetingInfoHandler() {
      axiosConfig
        .get(this.shareLinkUrl)
        .then((response) => {
          if (response.data.data.is_private && !response.data.data.is_guest_confirm) {
            if (this.isLoggedIn) {
              this.meetingInfo = response.data.data;
              this.newFormatDateHandler();
            } else {
              this.$toast.error(this.$t('auth_error'));
              this.$router.push({
                name: 'LoginPage',
                params: { locale: localStorage.getItem('locale') },
              });
            }
          } else {
            this.meetingInfo = response.data.data;
            this.newFormatDateHandler();
          }
        })
        .catch((error) => {
          if (error.response.status === 403) {
            if (this.isLoggedIn) {
              this.$toast.error(this.$t('confidential_meeting_toast'));
              this.$router.push({
                name: 'Dashboard',
                params: { locale: localStorage.getItem('locale') },
                query: { active: 'dashboard' },
              });
            } else {
              this.$toast.error(this.$t('auth_error'));
              this.$router.push({
                name: 'LoginPage',
                params: { locale: localStorage.getItem('locale') },
              });
            }
          } else {
            serverErrorsHandler(error);
          }
          throw error;
        });
    },
    newFormatDateHandler() {
      if (this.calendar === 'Solar' && this.meetingInfo !== null) {
        moment.locale('fa');
        this.newFormatDateStart = moment(this.meetingInfo.started_at.slice(0, 10)).format(
          'jDD jMMMM',
        );
        this.newFormatDateEnd = moment(this.meetingInfo.ended_at.slice(0, 10)).format('jDD jMMMM');
      } else {
        moment.locale('en');
        this.newFormatDateEnd = moment(this.meetingInfo.ended_at.slice(0, 10)).format('DD MMMM');
        this.newFormatDateStart = moment(this.meetingInfo.started_at.slice(0, 10)).format(
          'DD MMMM',
        );
      }
    },
  },
};
</script>

<style scoped>
.back-icon {
  @apply w-50 h-50 rounded flex left-50 cursor-pointer;
}
.top-bar {
  @apply flex flex-row items-center gap-x-10 lg:flex-wrap;
}
.meeting-detail-container {
  @apply flex flex-row mt-50 gap-20 sm:mt-10;
}
.introduction {
  @apply w-full h-full grid grid-cols-3 sm:grid-cols-1 bg-cover gap-x-20
     border-t border-default rounded rounded-tr-none rounded-tl-none bg-secondary;
}
.bullet-style {
  list-style-type: disc;
  @apply text-subtitle mt-20;
}
</style>
